import React from 'react';
import platePatternUtils from "../../../../../utils/plate-pattern-utils.js";
import {FaExternalLinkAlt} from "react-icons/fa";
import ProjectUtils from "../../../../../utils/project-utils.js";
import InfoGrid from "../../../../../components/info-grid/info-grid.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import useIndividualCatalogs from "../../hooks/use-individual-catalogs.js";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";

const API_URL = import.meta.env.VITE_API_URL;

const getOptionLabel = (options, value) =>
    options.find((option) => String(option.value) === String(value))?.label ?? value ?? "";

const formatNullableBoolean = (value) => {
    if (value === true) return "Sim";
    if (value === false) return "Não";
    return "";
};

const EncounterIndividualContent = ({ individuals, vehicles, showImages, setSelectedImage, setOpenImageModal, setModalAttach }) => {
    const navigate = navigateHook();
    const {
        individualColors,
        bodyShapes,
        eyeColors,
        hairTypes,
        hairColors,
        hairSizes,
        situations,
        educationDegrees,
        individualRegions,
        individualRegionDetails,
        systemNatures,
    } = useIndividualCatalogs();

    return (
        <>

            { individuals.map((individual, index) => {
                const cpf = individual?.cpf ?? "";
                const isValidCpf = String(cpf).replace(/\D/g, "").length === 11;
                const address = individual?.address ?? {};
                const individualRegionWrapper = individual?.individualRegionWrapper ?? [];
                const articles = individual?.articles ?? [];

                const attaches = individual?.attaches ?? [];
                const faceAttach = attaches.find((attach) => {
                    const attachId = attach?.attachId ?? attach?.attach_id ?? attach?.id;
                    return attachId && attachId === individual?.faceAttach;
                });
                const otherAttaches = attaches.filter((attach) => attach !== faceAttach);
                const deletedAttachesCount = individual?.deletedAttaches?.length ?? 0;

                const relatedVehicles = (vehicles ?? []).filter((vehicle) => (vehicle?.relatedIndividuals ?? []).some((ri) => ri?.individualId === individual?.id));
                const relatedVehicleLabels = relatedVehicles.map((vehicle) => {
                    const relation = (vehicle?.relatedIndividuals ?? []).find((ri) => ri?.individualId === individual?.id);
                    const relationLabel = relation?.relationship === "DRIVER" ? "Condutor" : relation?.relationship === "PASSENGER" ? "Passageiro" : "";
                    const plate = platePatternUtils.formatPlate(vehicle?.plate)

                    return `${relationLabel} - Placa: ${plate}`
                });

                const initialInfo = [
                    {
                        label: "CPF",
                        value: ProjectUtils.formatCpfToView(individual?.cpf) ?? ""
                    },
                    {
                        label: "Nome",
                        value: individual?.name ?? ""
                    },
                    {
                        label: "Nome social",
                        value: individual?.socialName ?? ""
                    },
                    {
                        label: "Apelido",
                        value: individual?.socialName ?? ""
                    },
                    {
                        label: "Sexo",
                        value: (individual?.sex === "FEMALE" ? "Feminino" : (individual?.sex === "MALE" ? "Masculino" : "")) ?? ""
                    },
                    {
                        label: "Data de nascimento",
                        value: ProjectUtils.formatDate(individual?.dtBirth ?? individual?.dt_birth) ?? ""
                    },
                ];

                const documentsInfo = [{
                    subTitle: "Documentos",
                    items: [
                        {
                            label: "RG",
                            value: individual?.rg ?? ""
                        },
                        {
                            label: "Emissor",
                            value: individual?.emitterRg ?? "",
                            className: "font-bolder"
                        },
                        {
                            label: "RG criminal",
                            value: individual?.criminalRg ?? ""
                        },
                        {
                            label: "CNH",
                            value: individual?.cnh ?? ""
                        },
                    ],
                }];

                const generalInfo = [{
                    subTitle: "Informações gerais",
                    items: [
                    {
                        label: "Estado civil",
                        value: getOptionLabel(optionsUtils.maritalStatus, individual?.maritalStatus)
                    },
                    {
                        label: "Nacionalidade",
                        value: individual?.nationality ?? ""
                    },
                    {
                        label: "Naturalidade",
                        value: individual?.cityOfBirth ?? "",
                    },
                    {
                        label: "Nome da mãe",
                        value: individual?.mother ?? ""
                    },
                    {
                        label: "Nome do pai",
                        value: individual?.father ?? ""
                    },
                    {
                        label: "Telefone",
                        value: individual?.phone ?? ""
                    },
                    {
                        label: "Operadora (Telefone)",
                        value: individual?.phoneProvider ?? ""
                    },
                    {
                        label: "Possui transtorno mental",
                        value: formatNullableBoolean(individual?.mentalDisorder)
                    },
                    {
                        label: "Dependência álcool/drogas",
                        value: formatNullableBoolean(individual?.alcoholDrugDependency)
                    },
                    ],
                }];

                const characteristicsInfo = [{
                    subTitle: "Características",
                    items: [
                        {label: "Altura (cm)", value: individual?.height ?? ""},
                        {label: "Cútis", value: getOptionLabel(individualColors, individual?.color)},
                        {label: "Compleição", value: getOptionLabel(bodyShapes, individual?.bodyShape)},
                        {label: "Cor dos olhos", value: getOptionLabel(eyeColors, individual?.eyes)},
                        {label: "Tipo dos cabelos", value: getOptionLabel(hairTypes, individual?.hairType)},
                        {label: "Cor dos cabelos", value: getOptionLabel(hairColors, individual?.hairColor)},
                        {label: "Comprimento dos cabelos", value: getOptionLabel(hairSizes, individual?.hairSize)},
                    ],
                }];

                const addressInfo = [
                    {
                        subTitle: "Endereço",
                        items: [
                            {
                                label: "Situação de rua",
                                value: individual?.homeless ? "SIM" : "Não"
                            },
                            {
                                label: "Logradouro",
                                value: address?.address ?? ""
                            },
                            {
                                label: "Número",
                                value: address?.number ??  ""
                            },
                            {
                                label: "Bairro",
                                value: address?.neighborhood ?? ""
                            },
                            {
                                label: "Município",
                                value: address?.city ?? ""
                            },
                            {
                                label: "Complemento",
                                value: address?.complement ?? ""
                            },
                            {
                                label: "CEP",
                                value: address?.zipCode ?? ""
                            },
                            {
                                label: "Tipo",
                                value: getOptionLabel(optionsUtils.addressType, address?.type)
                            },
                            {
                                label: "Data",
                                value: address?.date ? ProjectUtils.formatDate(address.date, "DD/MM/YYYY") : ""
                            },
                            {
                                label: "Latitude",
                                value: address?.lat ?? ""
                            },
                            {
                                label: "Longitude",
                                value: address?.lng ?? ""
                            },
                        ]
                    },
                ]

                const otherInformation = [{
                    subTitle: "Outras informações",
                    items: [
                        {label: "Grau de instrução", value: getOptionLabel(educationDegrees, individual?.educationDegree)},
                        {label: "Profissão", value: individual?.occupation ?? ""},
                        {label: "Função no crime", value: individual?.roleCrime ?? ""},
                        {label: "Situação", value: getOptionLabel(situations, individual?.situation)},
                        {label: "Rede social", value: individual?.socialNetwork ?? "", span: 2},
                        {label: "Informações do indivíduo", value: individual?.information ?? "", span: 3},
                    ],
                }];

                const vehicleInfo = [
                    {
                        subTitle: "Veículos associados",
                        span: 3,
                        items: [
                            {
                                label: "Veículos",
                                value: relatedVehicleLabels.join(", ") ?? ""
                            }
                        ]
                    }
                ]

                return (
                    <>
                        <section key={`individual-${individual?.id ?? cpf}`}>

                                <h2 className={`flex items-center! justify-center! gap-2 w-full rounded-[5px] border border-[#e0e0e0] bg-[#efefef] py-0! text-lg! text-[#4f4f4f] ${isValidCpf ? "cursor-pointer" : ""}`}
                                    title={isValidCpf ? "Ver indivíduo" : undefined}
                                    role={isValidCpf ? "button" : undefined}
                                    onClick={() => {
                                        if (!isValidCpf) return;
                                        navigate(`/individuals/form/${cpf}`, true);
                                    }}>

                                    <span className={'text-center! print:text-[19px]!'}>{`INDIVÍDUO - ${index+1}`} </span> { isValidCpf ? <FaExternalLinkAlt className="text-xs print:hidden"/> : null }
                                </h2>

                        </section>

                        <section className="grid grid-cols-12 gap-3">
                            <div className={`col-span-4 ${!showImages ? "print:hidden" : ""}`}>
                                <div className="relative flex h-[225px] max-h-[225px] w-full items-center justify-center overflow-hidden rounded border border-[#e0e0e0]">
                                    <img
                                        src={faceAttach
                                            ? `${API_URL}/attach/${faceAttach?.attachId ?? faceAttach?.attach_id ?? faceAttach?.id}`
                                            : getAssetPath("img/user.png")}
                                        alt="Foto da face"
                                        className={`max-h-[220px] max-w-full object-contain ${faceAttach ? "cursor-pointer" : ""}`}
                                        onClick={() => {
                                            const attachId =
                                                faceAttach?.attachId ??
                                                faceAttach?.attach_id ??
                                                faceAttach?.id;

                                            if (!attachId) return;

                                            setSelectedImage(`${API_URL}/attach/${attachId}`);
                                            setOpenImageModal(true);
                                        }}
                                    />
                                </div>
                            </div>

                            <InfoGrid title="" items={initialInfo} columns={2} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className={`px-0! col-span-8 self-start ${!showImages ? "print:col-span-12" : ""}`} contentClassName="space-y-1!"/>
                        </section>

                        <InfoGrid title="" items={documentsInfo} columns={3} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className="px-2!" contentClassName="space-y-1" subTitleClassName="!font-normal"/>

                        <InfoGrid title="" items={generalInfo} columns={3} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className="px-2!" contentClassName="space-y-1" subTitleClassName="!font-normal"/>

                        <InfoGrid title="" items={characteristicsInfo} columns={3} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className="px-2!" contentClassName="space-y-1" subTitleClassName="!font-normal"/>

                        {individualRegionWrapper.length > 0 && (
                            <section className="space-y-2 px-2">
                                <h3 className="!font-normal print:break-after-avoid!">Outras características</h3>
                                <div className="space-y-1 text-[13px]! text-[#4f4f4f] print:text-[15px]!">
                                    {individualRegionWrapper.map((region, regionIndex) => (
                                        <div
                                            key={`individual-region-${region?.itemId ?? "legacy"}-${regionIndex}`}
                                            className="grid grid-cols-3 gap-2"
                                        >
                                            {region?.local && <p>Local: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{getOptionLabel(individualRegions, region.local)}</span></p>}
                                            <p>Tipo: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{getOptionLabel(individualRegionDetails, region?.type)}</span></p>
                                            <p className={region?.local ? "" : "col-span-2"}>Descrição: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]! whitespace-pre-line">{region?.description ?? ""}</span></p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        <InfoGrid title="" items={addressInfo} columns={3} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className="px-2!" contentClassName="space-y-1" subTitleClassName="!font-normal"/>

                        <InfoGrid title="" items={otherInformation} columns={3} labelClassName="text-[#4F4F4F] text-[13px] print:text-[15px]!" valueClassName="print:text-[17px]!" className="px-2!" contentClassName="space-y-1" subTitleClassName="!font-normal"/>

                        {articles.length > 0 && (
                            <section className="space-y-2 px-2">
                                <h3 className="!font-normal print:break-after-avoid!">Artigos indiciados</h3>
                                <div className="space-y-1 text-[13px]! text-[#4f4f4f] print:text-[15px]!">
                                    {articles.map((article, articleIndex) => {
                                        const articleAttach = article?.attachId && attaches.find((attach) =>
                                            String(attach?.attachId ?? attach?.attach_id ?? attach?.id) === String(article.attachId)
                                        );

                                        return (
                                            <div
                                                key={`article-${article?.itemId ?? "legacy"}-${articleIndex}`}
                                                className="grid grid-cols-4 gap-2"
                                            >
                                                <p>Data: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{article?.date ? ProjectUtils.formatDate(article.date, "DD/MM/YYYY") : ""}</span></p>
                                                <p>Boletim: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{article?.bo ?? ""}</span></p>
                                                <p>Natureza: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{getOptionLabel(systemNatures, article?.article)}</span></p>
                                                <p>
                                                    Anexo: <span className="info text-[14px] font-normal text-[#161616] print:text-[17px]!">{articleAttach ? "Sim" : article?.attachId ? "Indisponível" : "Não"}</span>
                                                    {articleAttach && (
                                                        <button
                                                            type="button"
                                                            className="ml-2 cursor-pointer print:hidden"
                                                            title="Visualizar anexo"
                                                            onClick={() => setModalAttach({open: true, attach: articleAttach})}
                                                        >
                                                            <FaExternalLinkAlt/>
                                                        </button>
                                                    )}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        { relatedVehicleLabels.length ? (
                            <InfoGrid title="" items={vehicleInfo} columns={3} labelClassName={'text-[#4F4F4F] text-[13px] print:text-[15px]!'} valueClassName={'print:text-[17px]!'} className={'px-2!'} contentClassName={'space-y-1'}/>
                        ) : null}

                        { otherAttaches.length ? (
                            <article className={`space-y-3 px-2 mt-7 ${!showImages ? 'print:hidden' : ''}`}>
                                <div className={`text-[16px] font-semibold text-[#4F4F4F]`}>
                                    Anexos
                                </div>

                                <section className="flex flex-wrap gap-3 print:break-inside-avoid!">
                                    { otherAttaches.map((attach) => {
                                        const attachId = attach?.attachId ?? attach?.attach_id ?? attach?.id;
                                        const src = attachId ? `${API_URL}/attach/${attachId}` : "";
                                        const isDoc = attachId && attachId === individual?.documentAttach;
                                        const tagLabel = isDoc ? "Documento" : null;

                                        return (
                                            <div key={`ind-attach-${attachId}`}>

                                                <button
                                                    type="button"
                                                    className={"w-[320px] h-[240px] rounded border border-zinc-200 cursor-pointer"}
                                                    onClick={() => {
                                                        if (!attachId) return;
                                                        setSelectedImage(src);
                                                        setOpenImageModal(true);
                                                    }}
                                                >

                                                    <img src={src} alt="Anexo" className={"w-[320px] h-[240px] object-cover"}/>

                                                </button>

                                                { tagLabel && (
                                                    <div className="mt-1 flex justify-center print:hidden">
                                                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                                                          {tagLabel}
                                                        </span>
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}

                                </section>
                            </article>
                        ) : null}

                        { deletedAttachesCount > 0 && (
                            <div className={"print:hidden italic text-center! text-xs text-[#af233a]"}>
                                {deletedAttachesCount} {deletedAttachesCount === 1 ? "anexo indisponível" : "anexos indisponíveis"} devido a um problema na coleta ou transmissão dos dados.
                            </div>
                        )}
                    </>
                );
            })}

        </>
    );
};

export default EncounterIndividualContent;
